import { inject, Injectable } from '@angular/core';
import { EnvService } from '../env/env.service';

@Injectable({ providedIn: 'root' })
export class AceModeHighlightService {
  regExp = /\[aceModeHighlight\s+([^\]]+)\]/g;

  private env = inject(EnvService);

  aceModeHighlightParser(content: string): string {
    if ((this.env.get.brandConfig.designMode ?? 'ace') === 'default') {
      return content.replace(this.regExp, '');
    }

    return content.replace(this.regExp, (_match: string, attributes: string) => {
      // Extract individual attributes from the attributes string
      const headline = this.extractAttribute(attributes, 'headline');
      const description = this.extractAttribute(attributes, 'description');
      let linkUrl = this.extractAttribute(attributes, 'linkUrl');
      const linkText = this.extractAttribute(attributes, 'linkText');
      const badgeText = this.extractAttribute(attributes, 'badgeText');
      const arrowDirection = this.extractAttribute(attributes, 'arrowDirection');
      const hideOnMobile = this.extractAttribute(attributes, 'hideOnMobile');

      // If linkUrl contains HTML anchor tag (due to Redactor auto-linking), extract the href
      // Handle both escaped (\") and unescaped (") quote formats
      if (linkUrl && (linkUrl.includes('<a href=') || linkUrl.includes('href='))) {
        // Try to match href with escaped quotes first
        let hrefMatch = linkUrl.match(/href=\\"([^"]+)\\"/);
        if (!hrefMatch) {
          // Fallback to unescaped quotes
          hrefMatch = linkUrl.match(/href="([^"]+)"/);
        }
        if (hrefMatch) {
          linkUrl = hrefMatch[1];
        }
      }

      const escapedHeadline = this.escapeAttribute(headline || '');
      const escapedDescription = this.escapeAttribute(description || '');
      const escapedLinkUrl = this.escapeAttribute(linkUrl || '');
      const escapedLinkText = this.escapeAttribute(linkText || '');
      const escapedBadgeText = this.escapeAttribute(badgeText || '');
      const escapedArrowDirection = this.escapeAttribute(arrowDirection || '');
      const escapedHideOnMobile = this.escapeAttribute(hideOnMobile || '');

      return `<div class="ace-mode-highlight-placeholder"
                   data-component="aceModeHighlight"
                   ${headline ? `data-headline="${escapedHeadline}"` : ''}
                   ${description ? `data-description="${escapedDescription}"` : ''}
                   ${linkUrl ? `data-link-url="${escapedLinkUrl}"` : ''}
                   ${linkText ? `data-link-text="${escapedLinkText}"` : ''}
                   ${badgeText ? `data-badge-text="${escapedBadgeText}"` : ''}
                   ${arrowDirection ? `data-arrow-direction="${escapedArrowDirection}"` : ''}
                   ${hideOnMobile ? `data-hide-on-mobile="${escapedHideOnMobile}"` : ''}>
              </div>`;
    });
  }

  private extractAttribute(attributes: string, name: string): string {
    // Find the attribute by name
    const attrStart = attributes.indexOf(`${name}="`);
    if (attrStart === -1) return '';

    // Find the value start position
    const valueStart = attrStart + name.length + 2; // +2 for ="

    // Find the closing quote, accounting for nested quotes in HTML
    const quoteCount = 0;
    let inTag = false;
    let endPos = valueStart;

    for (let i = valueStart; i < attributes.length; i++) {
      const char = attributes[i];

      if (char === '<') inTag = true;
      if (char === '>') inTag = false;

      if (char === '"' && !inTag) {
        if (quoteCount === 0) {
          // This is the closing quote for our attribute
          endPos = i;
          break;
        }
      }
    }

    return attributes.substring(valueStart, endPos);
  }

  private escapeAttribute(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
